'use strict';

Object.defineProperty(exports, '__esModule', { value: true });

var React = require('react');
var client = require('@apollo/client');
var nthHookLoma = require('@narvar/nth-hook-loma');
var graphql = require('./graphql.js');
var proxyRetailerTranslations = require('./proxyRetailerTranslations.js');

function _interopNamespace(e) {
  if (e && e.__esModule) return e;
  var n = Object.create(null);
  if (e) {
    Object.keys(e).forEach(function (k) {
      if (k !== 'default') {
        var d = Object.getOwnPropertyDescriptor(e, k);
        Object.defineProperty(n, k, d.get ? d : {
          enumerable: true,
          get: function () { return e[k]; }
        });
      }
    });
  }
  n["default"] = e;
  return Object.freeze(n);
}

var React__namespace = /*#__PURE__*/_interopNamespace(React);

const TranslationsContext = /*#__PURE__*/React__namespace.createContext(undefined);
const LomaTranslationsConfigContext = /*#__PURE__*/React__namespace.createContext({
  product: 'narvarjs_translations',
  retailerMoniker: 'DEFAULT_RETAILER',
  versionName: 'narvar'
});
const LomaTranslationsConfigProvider = _ref => {
  let {
    children,
    ...props
  } = _ref;
  return /*#__PURE__*/React__namespace.createElement(LomaTranslationsConfigContext.Provider, {
    value: props
  }, children);
};
const nullRetailerTanslations = {}; // maintains object ref equality
const LomaTranslationsProvider = _ref2 => {
  let {
    children,
    translationKeys,
    product: productOverride,
    localeCountry: localeCountryOverride,
    status: statusOverride,
    category: categoryOverride
  } = _ref2;
  const client$1 = nthHookLoma.useLomaClient();
  const {
    product,
    retailerMoniker,
    localeLanguage,
    localeCountry,
    versionName,
    type,
    category,
    status,
    preventFouc,
    cache
  } = React__namespace.useContext(LomaTranslationsConfigContext);
  const stringifiedKeys = translationKeys.join('\n');
  const {
    loading,
    data
  } = client.useQuery(graphql["default"](stringifiedKeys), {
    client: client$1,
    variables: {
      product: productOverride || product,
      retailerMoniker,
      localeLanguage: localeLanguage || 'en',
      localeCountry: localeCountryOverride || localeCountry,
      versionName,
      type,
      category: categoryOverride || category,
      status: statusOverride || status,
      cache
    }
  });
  const translations = (data === null || data === void 0 ? void 0 : data.retailerTranslations) ?? nullRetailerTanslations;
  const value = React__namespace.useMemo(() => ({
    loading,
    translations: proxyRetailerTranslations["default"](translations, translationKeys)
  }), [loading, translations, translationKeys]);
  return /*#__PURE__*/React__namespace.createElement(TranslationsContext.Provider, {
    value
  }, loading && preventFouc ? /*#__PURE__*/React__namespace.createElement('span', {
    style: {
      visibility: 'hidden'
    }
  }, children) : children);
};
const TranslationsProviderContext = /*#__PURE__*/React__namespace.createContext(LomaTranslationsProvider);
const TranslationsProvider = props => {
  const {
    translationKeys
  } = props;
  if (!translationKeys.length) {
    throw new Error('translationKeys must be non-empty');
  }
  const TranslationsProvider = React__namespace.useContext(TranslationsProviderContext);
  return /*#__PURE__*/React__namespace.createElement(TranslationsProvider, props);
};
const withTranslationsProvider = (component, options) => {
  const {
    translationKeys
  } = options;
  const HoC = /*#__PURE__*/React__namespace.forwardRef((_ref3, ref) => {
    let {
      translations = {},
      ...props
    } = _ref3;
    const {
      localeCountry,
      status,
      category,
      product
    } = translations;
    return /*#__PURE__*/React__namespace.createElement(TranslationsProvider, {
      translationKeys,
      product,
      localeCountry,
      status,
      category
    }, /*#__PURE__*/React__namespace.createElement(component, {
      ...props,
      ref
    }));
  });
  const name = component.displayName ?? component.name;
  HoC.displayName = `WithTranslationsProvider(${name})`;
  return HoC;
};

/**
 * Optimal usage of `useTranslationsX` requires destructuring properties immediately:
 *
 * ```
 * const {
 *   loading,
 *   translations: {
 *     footerPrivacyLink,
 *     footerPrivacyLinkText
 *   }
 * } = useTranslationsX();
 * ```
 */
const useTranslationsX = () => {
  const context = React__namespace.useContext(TranslationsContext);
  if (!context) {
    throw new Error('useTranslationsX was called without a TranslationsContext');
  }
  return context;
};

/**
 * Optimal usage of `useTranslations` requires destructuring properties immediately:
 *
 * ```
 * const { footerPrivacyLink, footerPrivacyLinkText } = useTranslations();
 * ```
 */
const useTranslations = () => {
  const context = React__namespace.useContext(TranslationsContext);
  if (!context) {
    throw new Error('useTranslations was called without a TranslationsContext');
  }
  return context.translations;
};
const LocalTranslationsConfigContext = /*#__PURE__*/React__namespace.createContext({
  queryTranslations() {
    throw new Error('Not implemented');
  }
});
const LocalTranslationsProvider = _ref4 => {
  let {
    children,
    ...props
  } = _ref4;
  const config = React__namespace.useContext(LocalTranslationsConfigContext);
  const value = config.queryTranslations(props);
  return /*#__PURE__*/React__namespace.createElement(TranslationsContext.Provider, {
    value
  }, children);
};
const LocalTranslationsConfigProvider = _ref5 => {
  let {
    children,
    ...props
  } = _ref5;
  return /*#__PURE__*/React__namespace.createElement(TranslationsProviderContext.Provider, {
    value: LocalTranslationsProvider
  }, /*#__PURE__*/React__namespace.createElement(LocalTranslationsConfigContext.Provider, {
    value: props
  }, children));
};

exports.translationsQuery = graphql["default"];
exports.LocalTranslationsConfigProvider = LocalTranslationsConfigProvider;
exports.LocalTranslationsProvider = LocalTranslationsProvider;
exports.LomaTranslationsConfigProvider = LomaTranslationsConfigProvider;
exports.TranslationsProvider = TranslationsProvider;
exports.useTranslations = useTranslations;
exports.useTranslationsX = useTranslationsX;
exports.withTranslationsProvider = withTranslationsProvider;
